import {
  Badge,
  Banner,
  Button,
  DataTable,
  FlexBox,
  IconButton,
  Timestamp,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useAiSettingsVersionsQuery } from 'lib-knowledge-management-common/graphql';
import { useModal } from 'lib-knowledge-modal';
import Page from 'lib-knowledge-utils/components/page/index';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { useNavigate } from 'react-router-dom';

import { AiSettingsVersionSummary } from './components/versionSwitcher';
import { useAiSettingsLiveUpdates } from './hooks/useAiSettingsLiveUpdates';
import { useCanEditAiSettings } from './hooks/useCanEditAiSettings';
import { GoLiveModal } from './modals/goLiveModal';
import { VersionNameModal } from './modals/versionNameModal';

/** Every saved version of the AI suggestions settings, as in MIM's history. */
export function AiSuggestionsVersionsPage(): JSX.Element {
  const navigate = useNavigate();
  const { showModal } = useModal();
  useAiSettingsLiveUpdates();
  const { canEdit } = useCanEditAiSettings();

  const { data, loading, error } = useAiSettingsVersionsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const versions = data?.aiSettingsVersions ?? [];

  const viewVersion = (version: AiSettingsVersionSummary) =>
    navigate(routeGenerator.aiSuggestionsVersion({ versionId: version.id }));

  return (
    // A block, not a fragment: as a flex item of the settings page the section
    // would shrink, and the table, which hides its overflow, would be cut off.
    <div>
      <Page.Section
        title="Versions"
        titleMeta={
          <Badge size="small" variant="secondary">
            {versions.length}
          </Badge>
        }
        callout={
          <Banner
            variant="info"
            description={
              <>
                The AI uses the live version. Edit the draft to make changes,
                then create a version from it and make it live. Use the table
                below to go back to an earlier version.
              </>
            }
            actionSlot={
              <Button
                variant="secondary"
                onClick={() => navigate(routeGenerator.aiSuggestionsSettings())}
              >
                {canEdit ? 'Edit draft' : 'View draft'}
              </Button>
            }
          />
        }
      >
        <DataTable
          data={versions}
          tableRowIdSelector={(version) => version.id}
          tableStyleProps={{ stickyHeader: true }}
          tableStatusProps={{
            loading: loading && !data,
            loadingFailed: !!error && !data,
            ...(!loading &&
              !error &&
              versions.length === 0 && {
                emptyState: {
                  title: 'No versions',
                  description: 'Create a version to see it here.',
                },
              }),
          }}
          columns={[
            {
              label: 'Name',
              columnId: 'name',
              selector: (version) => version.name,
            },
            {
              label: 'Version',
              columnId: 'version',
              selector: (version) => `v${version.version}`,
            },
            {
              label: 'Created',
              columnId: 'createdAt',
              selector: (version) => (
                <Timestamp relative time={version.createdAt / 1000} />
              ),
            },
            {
              label: 'Status',
              columnId: 'status',
              selector: (version) =>
                version.isActive ? (
                  <Badge size="small" variant="success">
                    Live
                  </Badge>
                ) : null,
            },
            {
              label: '',
              style: { width: 0 },
              selector: (version) => (
                <FlexBox gap="200" justifyContent="flex-end">
                  <Tooltip content="View version">
                    <IconButton
                      icon="eye"
                      variant="tertiary"
                      aria-label={`View ${version.name}`}
                      onClick={() => viewVersion(version)}
                    />
                  </Tooltip>
                  {canEdit && (
                    <>
                      <Tooltip content="Go live" disabled={version.isActive}>
                        <IconButton
                          icon="upload"
                          variant="tertiary"
                          aria-label={`Go live with ${version.name}`}
                          disabled={version.isActive}
                          onClick={() =>
                            showModal(GoLiveModal, {
                              versionId: version.id,
                              name: version.name,
                            })
                          }
                        />
                      </Tooltip>
                      <Tooltip content="Rename version">
                        <IconButton
                          icon="pen"
                          variant="tertiary"
                          aria-label={`Rename ${version.name}`}
                          onClick={() =>
                            showModal(VersionNameModal, {
                              mode: 'rename',
                              versionId: version.id,
                              name: version.name,
                            })
                          }
                        />
                      </Tooltip>
                    </>
                  )}
                </FlexBox>
              ),
            },
          ]}
        />
      </Page.Section>
    </div>
  );
}
